import { useEffect } from "react";

/**
 * Keeps the screen on while a movement is open.
 *
 * Between two sets the phone sits on the bench; the default lock timeout means
 * unlocking it with chalky hands every ninety seconds. Supported from iOS 16.4;
 * where it is not, this quietly does nothing.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    const request = async () => {
      try {
        sentinel = await navigator.wakeLock.request("screen");
      } catch {
        /* denied, or the tab is not visible; nothing to recover from */
      }
    };

    // The lock is dropped whenever the tab is hidden, so it is taken again on
    // the way back.
    const onVisible = () => {
      if (document.visibilityState === "visible" && !released) void request();
    };

    void request();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release();
    };
  }, [active]);
}
